import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { DealSubmission, DealType, StorePreview, SubmissionInput, SubmissionPreview } from 'src/app/models/community';
import { ACCEPTED_IMAGE_TYPES, MAX_IMAGE_BYTES, StoredImage } from 'src/app/models/stored-image';
import { AuthService } from 'src/app/services/auth.service';
import { CommunityService } from 'src/app/services/community.service';
import { I18nService } from 'src/app/services/i18n.service';
import { ImagesService } from 'src/app/services/images.service';
import { InStoreDetection, InStoreScanService } from 'src/app/services/in-store-scan.service';
import { KNOWN_STORES } from 'src/app/shared/in-store-parser';
import { DialogContentLogin } from '../header-sticky/login-dialog/dialog-content-login';
import { POST_CATEGORIES } from '../my-account/account-sections';

type Step = 'link' | 'details' | 'done';
type Mode = 'online' | 'inStore';

// Photo d'une offre en magasin : aperçu local, fichier réduit (sans EXIF) pour l'analyse, URL une fois enregistrée par l'API
interface InStorePhoto {
  preview: string;
  file: File | null;
  url: string;
  busy: boolean;
}

const MAX_IN_STORE_PHOTOS = 3;
// Champs pré-remplis depuis la photo (badge « détecté » dans le formulaire)
type DetectedField = 'title' | 'priceDiscount' | 'price' | 'category' | 'startDate' | 'expirationDate' | 'storeName';

// Bouton « Poster » : le membre colle un lien, l'API reconnaît le marchand (et son lien d'affiliation),
// pré-remplit le formulaire, puis le deal part en modération dans le back office.
// Mode « En magasin » : pas de lien, le membre prend ou choisit 1 à 3 photos ; le navigateur y lit le code-barres et
// le texte de l'étiquette (InStoreScanService) pour pré-remplir le maximum de champs, le reste est saisi à la main.
@Component({
  selector: 'app-post-deal',
  templateUrl: './post-deal.component.html',
  styleUrls: ['./post-deal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PostDealComponent implements OnInit, OnDestroy {

  readonly categories = POST_CATEGORIES;
  readonly acceptedImageTypes = ACCEPTED_IMAGE_TYPES.join(',');
  readonly knownStores = KNOWN_STORES.map(([name]) => name);
  readonly maxPhotos = MAX_IN_STORE_PHOTOS;

  isLoggedIn = false;
  mode: Mode = 'online';
  step: Step = 'link';
  url = '';
  analyzing = false;
  submitting = false;
  error = '';
  preview: SubmissionPreview | null = null;
  submitted: DealSubmission | null = null;
  certified = false;
  pictureBroken = false;

  // Image : capturée sur la page de l'offre, choisie parmi les autres images trouvées, lien collé ou fichier joint.
  // Toutes passent par l'API qui les enregistre en base ; si c'est impossible, l'URL d'origine est gardée.
  pictureCandidates: string[] = [];
  selectedCandidate = '';
  pictureBusy = false;
  pictureError = '';
  pictureLink = '';
  showPictureLink = false;
  dragOver = false;

  form = this.emptyForm();

  // ----- Offre en magasin -----
  photos: InStorePhoto[] = [];
  photoError = '';
  scanning = false;
  scanStep: 'barcode' | 'text' = 'barcode';
  scanProgress = 0;
  detected: Partial<Record<DetectedField, boolean>> = {};
  detectedCount = 0;
  store = this.emptyStore();
  storePreview: StorePreview | null = null;
  locating = false;
  locationError = '';
  private storePreviewName = '';

  // Bouton « Coller » : seulement si le navigateur permet de lire le presse-papier
  canPaste = false;

  // Lien reçu par la liste de partage du téléphone (share_target du manifest), analysé dès que le membre est connecté
  private sharedUrl = '';
  private sharedTitle = '';

  private subscriptions = new Subscription();

  constructor(
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private communityService: CommunityService,
    private imagesService: ImagesService,
    private scanService: InStoreScanService,
    public i18n: I18nService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.canPaste = !!navigator.clipboard?.readText;
      this.readSharedLink();
      this.subscriptions.add(this.authService.loggedIn$.subscribe(loggedIn => {
        this.isLoggedIn = loggedIn;
        if (loggedIn && this.sharedUrl) {
          this.url = this.sharedUrl;
          this.sharedUrl = '';
          this.analyze();
        }
      }));
      if (this.sharedUrl && !this.isLoggedIn) {
        setTimeout(() => this.openLogin());
      }
    }
  }

  // Les applis qui partagent mettent le lien dans « url » ou dans « text » (souvent « titre https://… »)
  private readSharedLink(): void {
    const params = this.route.snapshot.queryParamMap;
    // /poster?mode=inStore (lien « Poster une offre en magasin » de /pres-de-moi) : onglet « En magasin » ouvert
    if (params.get('mode') === 'inStore') {
      this.mode = 'inStore';
    }
    const link = extractLink(params.get('url')) || extractLink(params.get('text'));
    if (!link) {
      return;
    }
    this.sharedUrl = link;
    this.sharedTitle = (params.get('title') || '').trim();
    this.url = link;
    // Paramètres retirés de l'adresse : un rechargement ou un retour ne relance pas l'analyse
    this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
  }

  async pasteLink(): Promise<void> {
    try {
      const link = extractLink(await navigator.clipboard.readText());
      if (!link) {
        this.error = this.i18n.t('post.pasteNoLink');
        return;
      }
      this.url = link;
      this.analyze();
    } catch {
      this.error = this.i18n.t('post.pasteNoLink');
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.photos.forEach(photo => URL.revokeObjectURL(photo.preview));
    if (isPlatformBrowser(this.platformId)) {
      this.scanService.release();
    }
  }

  get isInStore(): boolean {
    return this.mode === 'inStore';
  }

  setMode(mode: Mode): void {
    this.mode = mode;
    this.error = '';
  }

  private emptyStore() {
    return {
      name: '',
      city: '',
      latitude: null as number | null,
      longitude: null as number | null,
      accuracy: 0,
    };
  }

  private emptyForm() {
    return {
      title: '',
      dealType: 'goodPlan' as DealType,
      priceDiscount: null as number | null,
      price: null as number | null,
      deliveryPrice: '',
      promotionCode: '',
      category: '',
      pictureUrl: '',
      description: '',
      startDate: '',
      expirationDate: '',
    };
  }

  openLogin(): void {
    this.dialog.open(DialogContentLogin, { width: '550px' });
  }

  analyze(): void {
    const url = this.url.trim();
    if (!url) {
      this.error = this.i18n.t('post.required');
      return;
    }
    this.error = '';
    this.analyzing = true;
    this.communityService.preview(/^https?:\/\//i.test(url) ? url : 'https://' + url).subscribe({
      next: preview => {
        this.analyzing = false;
        this.preview = preview;
        this.url = preview.url;
        // Les champs déjà saisis par le membre ne sont jamais écrasés
        this.form = {
          ...this.form,
          title: this.form.title || preview.title || this.sharedTitle || '',
          description: this.form.description || preview.description || '',
          category: this.form.category || this.knownCategory(preview.category),
          pictureUrl: this.form.pictureUrl || preview.pictureUrl || '',
          priceDiscount: this.form.priceDiscount ?? preview.price,
          price: this.form.price ?? preview.oldPrice,
          deliveryPrice: this.form.deliveryPrice || this.formatDelivery(preview.deliveryPrice),
          expirationDate: this.form.expirationDate || preview.expirationDate || '',
        };
        this.pictureCandidates = preview.pictureCandidates || [];
        this.selectedCandidate = '';
        this.pictureBroken = false;
        this.pictureError = '';
        this.step = 'details';
      },
      error: err => {
        this.analyzing = false;
        this.error = err || this.i18n.t('common.errorOccurred');
      }
    });
  }

  changeUrl(): void {
    this.step = 'link';
    this.preview = null;
    this.error = '';
  }

  // ----- Offre en magasin : photos -----

  get photosBusy(): boolean {
    return this.photos.some(photo => photo.busy);
  }

  get readyPhotos(): InStorePhoto[] {
    return this.photos.filter(photo => !photo.busy && !!photo.url);
  }

  onPhotosSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = '';
    this.addPhotos(files);
  }

  // Photo réduite et sans EXIF dans le navigateur, puis enregistrée par l'API (stockage d'images de PromoDeep)
  async addPhotos(files: File[]): Promise<void> {
    this.photoError = '';
    const room = MAX_IN_STORE_PHOTOS - this.photos.length;
    if (files.length > room) {
      this.photoError = this.i18n.t('post.store.photoMax', { max: MAX_IN_STORE_PHOTOS });
    }
    for (const file of files.slice(0, Math.max(room, 0))) {
      if (!file.type.startsWith('image/')) {
        this.photoError = this.i18n.t('post.pictureBadType');
        continue;
      }
      const photo: InStorePhoto = { preview: URL.createObjectURL(file), file: null, url: '', busy: true };
      this.photos = [...this.photos, photo];
      try {
        photo.file = await this.scanService.preparePhoto(file);
        photo.url = (await firstValueFrom(this.imagesService.upload(photo.file))).url;
        photo.busy = false;
      } catch (err) {
        this.removePhoto(photo);
        this.photoError = typeof err === 'string' && err ? err : this.i18n.t('post.store.photoError');
      }
    }
  }

  removePhoto(photo: InStorePhoto): void {
    URL.revokeObjectURL(photo.preview);
    this.photos = this.photos.filter(p => p !== photo);
    if (photo.url && this.form.pictureUrl === photo.url) {
      this.form.pictureUrl = this.readyPhotos[0]?.url || '';
    }
    this.pictureCandidates = this.pictureCandidates.filter(candidate => candidate !== photo.url);
  }

  // Lecture du code-barres et du texte des photos, puis formulaire pré-rempli (jamais par-dessus une saisie du membre)
  async analyzePhotos(): Promise<void> {
    if (!this.readyPhotos.length || this.photosBusy || this.scanning) {
      return;
    }
    this.error = '';
    this.scanning = true;
    this.scanStep = 'barcode';
    this.scanProgress = 0;
    let detection: InStoreDetection | null = null;
    try {
      detection = await this.scanService.scan(this.readyPhotos.map(photo => photo.file!), (step, progress) => {
        this.scanStep = step;
        this.scanProgress = Math.round(progress * 100);
      });
    } catch {
      detection = null;
    }
    this.scanning = false;
    this.applyDetection(detection);
    this.goToStoreDetails();
  }

  // « Remplir à la main » : sans analyse
  skipAnalysis(): void {
    if (this.readyPhotos.length && !this.photosBusy) {
      this.applyDetection(null);
      this.goToStoreDetails();
    }
  }

  private goToStoreDetails(): void {
    this.step = 'details';
    this.loadStorePreview();
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  private applyDetection(detection: InStoreDetection | null): void {
    const photoUrls = this.readyPhotos.map(photo => photo.url);
    this.detected = {};
    if (detection) {
      const empty = (value: unknown) => value === null || value === undefined || value === '';
      const form = this.form as Record<string, unknown>;
      const fill = (field: Exclude<DetectedField, 'storeName'>, value: unknown) => {
        if (!empty(value) && empty(form[field])) {
          form[field] = value;
          this.detected[field] = true;
        }
      };
      fill('title', detection.title.length >= 5 ? detection.title : '');
      fill('priceDiscount', detection.priceDiscount);
      fill('price', detection.price !== null && detection.price !== detection.priceDiscount ? detection.price : null);
      fill('category', this.knownCategory(detection.category));
      fill('startDate', detection.startDate);
      fill('expirationDate', detection.expirationDate);
      if (detection.storeName && !this.store.name.trim()) {
        this.store.name = detection.storeName;
        this.detected.storeName = true;
      }
    }
    this.detectedCount = Object.keys(this.detected).length;
    this.form.dealType = 'goodPlan';
    this.pictureCandidates = [...photoUrls, detection?.productPictureUrl || ''].filter(Boolean);
    if (!this.form.pictureUrl || !photoUrls.includes(this.form.pictureUrl)) {
      this.form.pictureUrl = photoUrls[0] || '';
      this.selectedCandidate = this.form.pictureUrl;
    }
    this.pictureBroken = false;
    this.pictureError = '';
  }

  changePhotos(): void {
    this.step = 'link';
    this.error = '';
  }

  // ----- Offre en magasin : enseigne et position -----

  // Enseigne reconnue parmi les annonceurs (logo, partenaire) et points estimés
  loadStorePreview(): void {
    const name = this.store.name.trim();
    if (name === this.storePreviewName) {
      return;
    }
    this.storePreviewName = name;
    if (name.length < 2) {
      this.storePreview = null;
      return;
    }
    this.communityService.storePreview(name).subscribe({
      next: preview => {
        if (this.storePreviewName === name) {
          this.storePreview = preview;
        }
      },
      error: () => this.storePreview = null,
    });
  }

  // Position demandée seulement sur ce bouton (le navigateur demande l'accord du membre)
  locate(): void {
    this.locationError = '';
    if (!isPlatformBrowser(this.platformId) || !navigator.geolocation) {
      this.locationError = this.i18n.t('post.store.locationUnavailable');
      return;
    }
    this.locating = true;
    navigator.geolocation.getCurrentPosition(position => {
      this.locating = false;
      this.store.latitude = position.coords.latitude;
      this.store.longitude = position.coords.longitude;
      this.store.accuracy = Math.round(position.coords.accuracy);
    }, error => {
      this.locating = false;
      this.locationError = this.i18n.t(error.code === error.PERMISSION_DENIED ? 'post.store.locationDenied' : 'post.store.locationUnavailable');
    }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 });
  }

  clearLocation(): void {
    this.store.latitude = null;
    this.store.longitude = null;
    this.store.accuracy = 0;
  }

  get hasLocation(): boolean {
    return this.store.latitude !== null && this.store.longitude !== null;
  }

  get estimatedPoints(): number {
    return (this.isInStore ? this.storePreview?.estimatedPoints : this.preview?.estimatedPoints) || 0;
  }

  // Catégorie proposée par l'API, gardée seulement si elle existe dans la liste du formulaire
  private knownCategory(category: string | undefined): string {
    return this.categories.find(c => c.value === category)?.value || '';
  }

  private formatDelivery(price: number | null): string {
    if (price === null || price === undefined) {
      return '';
    }
    return price === 0 ? this.i18n.t('post.freeDelivery') : price.toFixed(2).replace('.', ',') + ' €';
  }

  // ----- Image -----

  selectCandidate(url: string): void {
    // Photo du membre, déjà enregistrée par l'API
    if (this.readyPhotos.some(photo => photo.url === url)) {
      this.form.pictureUrl = url;
      this.selectedCandidate = url;
      this.pictureBroken = false;
      return;
    }
    this.storePicture(this.imagesService.importFromUrl(url, this.preview?.url), url, url);
  }

  // Miniature externe illisible (anti-hotlink, image supprimée…) : retirée de la liste
  hideCandidate(url: string): void {
    this.pictureCandidates = this.pictureCandidates.filter(candidate => candidate !== url);
  }

  importPictureLink(): void {
    const url = this.pictureLink.trim();
    if (!url) {
      return;
    }
    this.storePicture(this.imagesService.importFromUrl(url, this.preview?.url), '', url, () => {
      this.pictureLink = '';
      this.showPictureLink = false;
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) {
      this.uploadPicture(file);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = true;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.uploadPicture(file);
    }
  }

  private uploadPicture(file: File): void {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      this.pictureError = this.i18n.t('post.pictureBadType');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      this.pictureError = this.i18n.t('post.pictureTooBig');
      return;
    }
    this.storePicture(this.imagesService.upload(file), '', '');
  }

  // fallbackUrl : image non enregistrable en base (lien collé, miniature) -> on garde son URL d'origine, comme avant
  private storePicture(request: Observable<StoredImage>, candidate: string, fallbackUrl: string, onSuccess?: () => void): void {
    const apply = (url: string) => {
      this.pictureBusy = false;
      this.form.pictureUrl = url;
      this.selectedCandidate = candidate;
      this.pictureBroken = false;
      onSuccess?.();
    };
    this.pictureError = '';
    this.pictureBusy = true;
    request.subscribe({
      next: image => apply(image.url),
      error: err => {
        if (fallbackUrl) {
          apply(fallbackUrl);
          return;
        }
        this.pictureBusy = false;
        this.pictureError = err || this.i18n.t('common.errorOccurred');
      }
    });
  }

  removePicture(): void {
    this.form.pictureUrl = '';
    this.selectedCandidate = '';
    this.pictureBroken = false;
    this.pictureError = '';
  }

  get discountPercent(): number | null {
    const { price, priceDiscount } = this.form;
    if (!price || priceDiscount === null || priceDiscount === undefined || priceDiscount >= price || price <= 0) {
      return null;
    }
    return Math.round((1 - priceDiscount / price) * 100);
  }

  get titleInvalid(): boolean {
    const length = this.form.title.trim().length;
    return length < 5 || length > 200;
  }

  get canSubmit(): boolean {
    const common = !this.submitting && !this.pictureBusy && this.certified && !this.titleInvalid;
    if (this.isInStore) {
      return common && !this.photosBusy && this.readyPhotos.length > 0 && this.store.name.trim().length >= 2;
    }
    return common && !this.preview?.duplicate
      && (this.form.dealType !== 'promotionCode' || !!this.form.promotionCode.trim());
  }

  submit(): void {
    if (!this.canSubmit || (!this.preview && !this.isInStore)) {
      return;
    }
    this.error = '';
    this.submitting = true;
    const store: Partial<SubmissionInput> = this.isInStore
      ? {
        inStore: true,
        photos: this.readyPhotos.map(photo => photo.url),
        storeName: this.store.name.trim(),
        storeCity: this.store.city.trim(),
        latitude: this.store.latitude,
        longitude: this.store.longitude,
      }
      : {};
    const input: SubmissionInput = {
      ...store,
      url: this.isInStore ? '' : this.preview!.url,
      title: this.form.title.trim(),
      dealType: this.form.dealType,
      price: this.form.price ?? null,
      priceDiscount: this.form.priceDiscount ?? null,
      deliveryPrice: this.isInStore ? '' : this.form.deliveryPrice,
      promotionCode: this.form.dealType === 'promotionCode' ? this.form.promotionCode.trim() : '',
      description: this.form.description,
      pictureUrl: this.pictureBroken ? '' : this.form.pictureUrl.trim(),
      category: this.form.category,
      startDate: this.form.startDate || null,
      expirationDate: this.form.expirationDate || null,
    };
    this.communityService.submit(input).subscribe({
      next: submission => {
        this.submitting = false;
        this.submitted = submission;
        this.step = 'done';
        if (isPlatformBrowser(this.platformId)) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      },
      error: err => {
        this.submitting = false;
        this.error = err || this.i18n.t('common.errorOccurred');
      }
    });
  }

  postAnother(): void {
    this.form = this.emptyForm();
    this.url = '';
    this.preview = null;
    this.submitted = null;
    this.certified = false;
    this.error = '';
    this.pictureCandidates = [];
    this.selectedCandidate = '';
    this.pictureError = '';
    this.pictureLink = '';
    this.showPictureLink = false;
    this.sharedTitle = '';
    this.photos.forEach(photo => URL.revokeObjectURL(photo.preview));
    this.photos = [];
    this.photoError = '';
    this.detected = {};
    this.detectedCount = 0;
    this.store = this.emptyStore();
    this.storePreview = null;
    this.storePreviewName = '';
    this.locationError = '';
    this.step = 'link';
  }
}

// Premier lien http(s) d'un texte partagé ou collé, sans la ponctuation qui le suit
export function extractLink(text: string | null | undefined): string {
  const match = (text || '').match(/https?:\/\/[^\s<>"]+/i);
  return match ? match[0].replace(/[).,;!?»'"]+$/, '') : '';
}
